import React, { useState, useCallback, useMemo } from 'react';
import { 
  DocumentTextIcon,
  PhotoIcon,
  FilmIcon,
  MusicalNoteIcon,
  ArchiveBoxIcon,
  ArrowUturnLeftIcon,
  PresentationChartBarIcon,
  CodeBracketIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  PencilSquareIcon,
  TrashIcon,
  ShareIcon,
  StarIcon,
  ClockIcon,
  CheckIcon,
  XMarkIcon,
  TagIcon,
  PlusIcon,
  FolderIcon
} from '@heroicons/react/24/outline';
import { DocumentStorage } from '../../types/documentStorage';
import { formatDistanceToNow } from 'date-fns';
import { hu } from 'date-fns/locale';
import FilePreview from './FilePreview';
import { useDocumentTags } from '../../hooks/useDocumentTags';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';

interface ModernDocumentGridProps {
  documents: DocumentStorage[];
  loading: boolean;
  viewMode: 'grid' | 'list';
  selectedDocuments: string[];
  onSelectDocument: (documentId: string) => void;
  onSelectAll: (selected: boolean) => void;
  onView: (document: DocumentStorage) => void;
  onEdit: (document: DocumentStorage) => void;
  onDelete: (document: DocumentStorage) => void;
  onDownload: (document: DocumentStorage) => void;
  onArchive?: (document: DocumentStorage) => void;
  onRestore?: (document: DocumentStorage) => void;
  onShare?: (document: DocumentStorage) => void;
  onAddTag?: (document: DocumentStorage, tag: string) => void;
  onRemoveTag?: (document: DocumentStorage, tagId: string) => void;
  onBulkDelete?: (documentIds: string[]) => void;
  onBulkDownload?: (documentIds: string[]) => void;
  onMove?: (document: DocumentStorage) => void;
}

export default function ModernDocumentGrid({
  documents,
  loading,
  viewMode,
  selectedDocuments,
  onSelectDocument,
  onSelectAll,
  onView,
  onEdit,
  onDelete,
  onDownload,
  onArchive,
  onRestore,
  onShare,
  onAddTag,
  onRemoveTag,
  onBulkDelete,
  onBulkDownload,
  onMove
}: ModernDocumentGridProps) {
  const [draggedOver, setDraggedOver] = useState<string | null>(null);
  const [showTagInput, setShowTagInput] = useState<string | null>(null); // Document ID for which tag input is shown
  const [newTag, setNewTag] = useState('');
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const { tags: documentTags } = useDocumentTags();

  const getFileIcon = (fileType?: string, fileName?: string) => {
    if (!fileType && !fileName) return DocumentTextIcon;
    
    const type = fileType?.toLowerCase() || fileName?.split('.').pop()?.toLowerCase();
    
    if (type?.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(type || '')) {
      return PhotoIcon;
    }
    if (type?.includes('video') || ['mp4', 'avi', 'mov', 'wmv', 'flv'].includes(type || '')) {
      return FilmIcon;
    }
    if (type?.includes('audio') || ['mp3', 'wav', 'flac', 'aac'].includes(type || '')) {
      return MusicalNoteIcon;
    }
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(type || '')) {
      return ArchiveBoxIcon;
    }
    if (['ppt', 'pptx', 'key'].includes(type || '')) {
      return PresentationChartBarIcon;
    }
    if (['js', 'ts', 'html', 'css', 'json', 'xml', 'py', 'java', 'cpp'].includes(type || '')) {
      return CodeBracketIcon;
    }
    
    return DocumentTextIcon;
  };

  const getFileTypeColor = (fileType?: string, fileName?: string) => {
    const type = fileType?.toLowerCase() || fileName?.split('.').pop()?.toLowerCase();
    
    if (type?.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(type || '')) {
      return '#10b981'; // green
    }
    if (type?.includes('video') || ['mp4', 'avi', 'mov', 'wmv', 'flv'].includes(type || '')) {
      return '#f59e0b'; // amber
    }
    if (type?.includes('audio') || ['mp3', 'wav', 'flac', 'aac'].includes(type || '')) {
      return '#8b5cf6'; // violet
    }
    if (['pdf'].includes(type || '')) {
      return '#dc2626'; // red
    }
    if (['doc', 'docx'].includes(type || '')) {
      return '#2563eb'; // blue
    }
    if (['xls', 'xlsx'].includes(type || '')) {
      return '#059669'; // emerald
    }
    if (['ppt', 'pptx'].includes(type || '')) {
      return '#ea580c'; // orange
    }
    
    return '#6b7280'; // gray
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (date: Date) => {
    return formatDistanceToNow(date, { addSuffix: true, locale: hu })
      .replace('körülbelül ', '')
      .replace(' ezelőtt', '')
      .replace('órával', 'órája')
      .replace('nappal', 'napja')
      .replace('héttel', 'hete')
      .replace('hónappal', 'hónapja')
      .replace('évvel', 'éve')
      .replace('perccel', 'perce')
      .replace('másodperccel', 'másodperce');
  };

  const handleAddTag = (document: DocumentStorage, tagIdOrName: string) => {
    const tagValue = tagIdOrName.trim();
    if (!tagValue) return;
    
    if (onAddTag) {
      onAddTag(document, tagValue);
    }
    
    // Clear input and close panel
    setNewTag('');
    setShowTagInput(null);
  };

  const handleBulkDownload = () => {
    if (onBulkDownload && selectedDocuments.length > 0) {
      onBulkDownload(selectedDocuments);
    }
  };

  const handleBulkDelete = () => {
    if (onBulkDelete && selectedDocuments.length > 0) {
      setShowBulkDeleteModal(true);
    }
  };

  const confirmBulkDelete = () => {
    if (onBulkDelete && selectedDocuments.length > 0) {
      onBulkDelete(selectedDocuments);
      setShowBulkDeleteModal(false);
    }
  };

  // Get tag color from documentTags (by ID or name)
  const getTagColor = (tagIdOrName: string): string => {
    const tag = documentTags.find(t => 
      t.id === tagIdOrName ||
      t.name.toLowerCase() === tagIdOrName.toLowerCase() || 
      (t.title && t.title.toLowerCase() === tagIdOrName.toLowerCase())
    );
    return tag?.color || '#6b7280'; // Default gray color
  };

  const allSelected = documents.length > 0 && selectedDocuments.length === documents.length;
  const someSelected = selectedDocuments.length > 0 && selectedDocuments.length < documents.length;

  if (loading) {
    return (
      <div className="space-y-4">
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 animate-pulse">
                <div className="w-full h-32 bg-gray-200 dark:bg-gray-700 rounded-lg mb-3"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 animate-pulse">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="text-center py-12">
        <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          Nincsenek dokumentumok
        </h3>
        <p className="text-gray-500 dark:text-gray-400">
          Ebben a mappában még nincsenek dokumentumok.
        </p>
      </div>
    );
  }

  if (viewMode === 'grid') {
    return (
      <div className="space-y-4">
        {/* Bulk Actions */}
        {selectedDocuments.length > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  {selectedDocuments.length} / {documents.length} dokumentum kiválasztva
                </span>
                <div className="flex items-center space-x-2">
                  {selectedDocuments.length < documents.length && (
                    <button
                      onClick={() => onSelectAll(true)}
                      className="text-sm text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100"
                    >
                      Összes kijelölése
                    </button>
                  )}
                  <button
                    onClick={() => onSelectAll(false)}
                    className="text-sm text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100"
                  >
                    Kijelölés törlése
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button 
                  onClick={handleBulkDownload}
                  className="p-2 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800 rounded-lg transition-colors"
                  title="Kiválasztott dokumentumok letöltése"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                </button>
                <button 
                  onClick={handleBulkDelete}
                  className="p-2 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-800 rounded-lg transition-colors"
                  title="Kiválasztott dokumentumok törlése"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Grid View */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {documents.map((document) => {
            const FileIcon = getFileIcon(document.fileType, document.fileName);
            const fileColor = getFileTypeColor(document.fileType, document.fileName);
            const isSelected = selectedDocuments.includes(document.id);
            
            return (
              <div
                key={document.id}
                className={`
                  group relative bg-white dark:bg-gray-800 rounded-xl border transition-all duration-200 hover:shadow-lg hover:-translate-y-1
                  ${isSelected 
                    ? 'border-blue-500 ring-2 ring-blue-500/20 shadow-lg' 
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }
                `}
              >
                {/* Selection Checkbox */}
                <div className="absolute top-3 left-3 z-10">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectDocument(document.id);
                    }}
                    className={`
                      w-5 h-5 rounded border-2 flex items-center justify-center transition-colors
                      ${isSelected 
                        ? 'bg-blue-500 border-blue-500 text-white' 
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 opacity-0 group-hover:opacity-100'
                      }
                    `}
                  >
                    {isSelected && <CheckIcon className="w-3 h-3" />}
                  </button>
                </div>

                {/* File Preview */}
                <div className="p-4 pb-2">
                  <div className="relative w-full h-32 mb-3 overflow-hidden">
                    <FilePreview
                      document={document}
                      className="w-full h-full"
                      fallbackColor={fileColor}
                      FileIcon={FileIcon}
                    />
                    
                    {/* File Type Badge */}
                    <div className="absolute top-2 right-2">
                      <span 
                        className="px-2 py-1 text-xs font-medium text-white rounded-full shadow-lg backdrop-blur-sm"
                        style={{ backgroundColor: fileColor }}
                      >
                        {document.fileName?.split('.').pop()?.toUpperCase() || 'FILE'}
                      </span>
                    </div>
                  </div>

                  {/* Document Info - Kattintható */}
                  <div className="space-y-2">
                    <h3 
                      className="font-semibold text-gray-900 dark:text-gray-100 truncate text-sm cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        onView(document);
                      }}
                    >
                      {document.title}
                    </h3>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>{formatFileSize(document.fileSize)}</span>
                      <span>{formatDate(document.createdAt)}</span>
                    </div>

                    <div className="flex flex-wrap gap-1 mt-2">
                      {document.tags && document.tags.length > 0 ? (
                        <>
                          {document.tags.slice(0, 4).map((tagId) => {
                            const tag = documentTags.find(t => t.id === tagId);
                            if (!tag) return null; // Skip if tag not found
                            const tagColor = getTagColor(tagId);
                            const displayName = tag.title || tag.name;
                            return (
                              <span 
                                key={tagId}
                                className="group inline-flex items-center px-2 py-0.5 text-xs font-medium text-white rounded-full shadow-sm hover:shadow-md transition-all duration-200"
                                style={{ 
                                  backgroundColor: tagColor,
                                  color: 'white'
                                }}
                                title={displayName}
                              >
                                <span className="w-1.5 h-1.5 bg-white/30 rounded-full mr-1.5"></span>
                                {displayName}
                                {onRemoveTag && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onRemoveTag(document, tagId);
                                    }}
                                    className="ml-1 opacity-0 group-hover:opacity-100 hover:bg-white/20 rounded-full p-0.5 transition-all duration-200"
                                    title="Címke eltávolítása"
                                  >
                                    <XMarkIcon className="w-2.5 h-2.5" />
                                  </button>
                                )}
                              </span>
                            );
                          })}
                          {document.tags.length > 4 && (
                            <span 
                              className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-gray-500 text-white rounded-full shadow-sm"
                              title={`További ${document.tags.length - 4} címke: ${document.tags.slice(4).map(tagId => {
                                const tag = documentTags.find(t => t.id === tagId);
                                return tag?.title || tag?.name || tagId;
                              }).join(', ')}`}
                            >
                              +{document.tags.length - 4}
                            </span>
                          )}
                          {onAddTag && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowTagInput(showTagInput === document.id ? null : document.id);
                              }}
                              className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-500 hover:text-purple-600 bg-gray-100 hover:bg-purple-50 dark:bg-gray-700 dark:text-gray-400 dark:hover:text-purple-400 dark:hover:bg-purple-900/20 rounded-full border border-dashed border-gray-300 hover:border-purple-300 dark:border-gray-600 dark:hover:border-purple-600 transition-all duration-200"
                              title="Címke hozzáadása"
                            >
                              <PlusIcon className="w-3 h-3" />
                            </button>
                          )}
                        </>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400 italic">Nincs címke</span>
                          {onAddTag && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowTagInput(showTagInput === document.id ? null : document.id);
                              }}
                              className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-500 hover:text-purple-600 bg-gray-100 hover:bg-purple-50 dark:bg-gray-700 dark:text-gray-400 dark:hover:text-purple-400 dark:hover:bg-purple-900/20 rounded-full border border-dashed border-gray-300 hover:border-purple-300 dark:border-gray-600 dark:hover:border-purple-600 transition-all duration-200"
                              title="Első címke hozzáadása"
                            >
                              <PlusIcon className="w-3 h-3 mr-1" />
                              <span>Címke</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="document-actions absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-1">
                    {/* Első sor - gombok */}
                    <div className="flex items-center space-x-1 mb-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onView(document);
                        }}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded transition-colors"
                        title="Részletek"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                      {document.downloadUrl && (document.fileType === 'application/pdf' || document.fileType?.startsWith('image/')) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(document.downloadUrl, '_blank');
                          }}
                          className="p-2 text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors"
                          title="Preview (új ablak)"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDownload(document);
                        }}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                        title="Letöltés"
                      >
                        <ArrowDownTrayIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(document);
                        }}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                        title="Szerkesztés"
                      >
                        <PencilSquareIcon className="w-5 h-5" />
                      </button>
                    </div>
                    
                    {/* Második sor - 3 gomb */}
                    <div className="flex items-center space-x-1">
                      {onMove && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onMove(document);
                          }}
                          className="p-2 text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors"
                          title="Áthelyezés mappába"
                        >
                          <FolderIcon className="w-5 h-5" />
                        </button>
                      )}
                      {document.status === 'active' && onArchive && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onArchive(document);
                          }}
                          className="p-2 text-gray-600 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded transition-colors"
                          title="Archiválás"
                        >
                          <ArchiveBoxIcon className="w-5 h-5" />
                        </button>
                      )}
                      {document.status === 'archived' && onRestore && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onRestore(document);
                          }}
                          className="p-2 text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                          title="Visszaállítás"
                        >
                          <ArrowUturnLeftIcon className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(document);
                        }}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Törlés"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Tag Input Panel for Grid View */}
        {showTagInput && onAddTag && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowTagInput(null)}>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Címke hozzáadása
                </h3>
                
                {/* Available Tags */}
                {documentTags.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Elérhető címkék:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {documentTags.slice(0, 12).map((tag) => (
                        <button
                          key={tag.id}
                          onClick={() => {
                            const document = documents.find(d => d.id === showTagInput);
                            if (document) handleAddTag(document, tag.id);
                          }}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-full hover:scale-105 transition-transform duration-200 shadow-sm"
                          style={{ 
                            backgroundColor: tag.color,
                            color: 'white'
                          }}
                          title={`"${tag.title || tag.name}" hozzáadása (${tag.count} dokumentumban használva)`}
                        >
                          <span className="w-1.5 h-1.5 bg-white/30 rounded-full mr-2"></span>
                          {tag.title || tag.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* New Tag Input */}
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Új címke létrehozása:
                  </p>
                  <div className="flex items-center space-x-3">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newTag.trim()) {
                          const document = documents.find(d => d.id === showTagInput);
                          if (document) handleAddTag(document, newTag.trim());
                        } else if (e.key === 'Escape') {
                          setShowTagInput(null);
                          setNewTag('');
                        }
                      }}
                      placeholder="Új címke neve..."
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      autoFocus
                    />
                    <button
                      onClick={() => {
                        if (newTag.trim()) {
                          const document = documents.find(d => d.id === showTagInput);
                          if (document) handleAddTag(document, newTag.trim());
                        }
                      }}
                      disabled={!newTag.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors"
                      title="Címke hozzáadása"
                    >
                      <PlusIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Close Button */}
                <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-600">
                  <button
                    onClick={() => {
                      setShowTagInput(null);
                      setNewTag('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                  >
                    Bezárás
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-4">
      {/* Bulk Actions */}
      {selectedDocuments.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {selectedDocuments.length} / {documents.length} dokumentum kiválasztva
              </span>
              <div className="flex items-center space-x-2">
                {selectedDocuments.length < documents.length && (
                  <button
                    onClick={() => onSelectAll(true)}
                    className="text-sm text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100"
                  >
                    Összes kijelölése
                  </button>
                )}
                <button
                  onClick={() => onSelectAll(false)}
                  className="text-sm text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100"
                >
                  Kijelölés törlése
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button 
                onClick={handleBulkDownload}
                className="p-2 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800 rounded-lg transition-colors"
                title="Kiválasztott dokumentumok letöltése"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
              </button>
              <button 
                onClick={handleBulkDelete}
                className="p-2 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-800 rounded-lg transition-colors"
                title="Kiválasztott dokumentumok törlése"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Header */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
        <div className="flex items-center space-x-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={allSelected}
              ref={(input) => {
                if (input) input.indeterminate = someSelected;
              }}
              onChange={(e) => onSelectAll(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
          </label>
          <div className="flex-1 grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            <div className="col-span-3">Név</div>
            <div className="col-span-4">Címkék</div>
            <div className="col-span-1">Méret</div>
            <div className="col-span-1">Típus</div>
            <div className="col-span-2">Módosítva</div>
            <div className="col-span-1">Műveletek</div>
          </div>
        </div>
      </div>

      {/* List Items - Kompakt */}
      <div className="space-y-1">
        {documents.map((document) => {
          const FileIcon = getFileIcon(document.fileType, document.fileName);
          const fileColor = getFileTypeColor(document.fileType, document.fileName);
          const isSelected = selectedDocuments.includes(document.id);
          
          return (
            <div
              key={document.id}
              className={`
                group bg-white dark:bg-gray-800 rounded-lg border transition-all duration-200 hover:shadow-md
                ${isSelected 
                  ? 'border-blue-500 ring-1 ring-blue-500/20 bg-blue-50/30 dark:bg-blue-900/10' 
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }
              `}
            >
              <div className="px-2 py-1.5">
                <div className="flex items-center gap-2">
                  <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => onSelectDocument(document.id)}
                      className={`
                        w-4 h-4 rounded border-2 flex items-center justify-center transition-all duration-200
                        ${isSelected 
                          ? 'bg-blue-500 border-blue-500 text-white' 
                          : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
                        }
                      `}
                    >
                      {isSelected && <span className="text-[10px]">✓</span>}
                    </button>
                  </div>
                  
                  <div className="flex-1 grid grid-cols-12 gap-2 items-center">
                    {/* Name - Kattintható */}
                    <div className="col-span-3 flex items-center gap-2 min-w-0">
                      <div className="w-8 h-8 rounded-lg flex-shrink-0 overflow-hidden">
                        <FilePreview
                          document={document}
                          className="w-full h-full"
                          fallbackColor={fileColor}
                          FileIcon={FileIcon}
                        />
                      </div>
                      <div 
                        className="min-w-0 flex-1 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          onView(document);
                        }}
                      >
                        <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                          {document.title}
                        </p>
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                          {document.fileName}
                        </p>
                      </div>
                    </div>
                    
                    {/* Tags - Több hely */}
                    <div className="col-span-4 min-w-0">
                      <div className="flex flex-wrap gap-1 items-center">
                        {document.tags && document.tags.length > 0 ? (
                          <>
                            {document.tags.slice(0, 4).map((tagId) => {
                              const tag = documentTags.find(t => t.id === tagId);
                              if (!tag) return null; // Skip if tag not found
                              const tagColor = getTagColor(tagId);
                              const displayName = tag.title || tag.name;
                              return (
                                <span 
                                  key={tagId}
                                  className="group/tag inline-flex items-center px-2 py-1 text-xs font-medium text-white rounded-full hover:pr-1 transition-all shadow-sm"
                                  style={{ 
                                    backgroundColor: tagColor,
                                    color: 'white'
                                  }}
                                  title={displayName}
                                >
                                  <span className="group-hover/tag:mr-1">
                                    {displayName.length > 12 ? displayName.substring(0, 12) + '...' : displayName}
                                  </span>
                                  {onRemoveTag && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onRemoveTag(document, tagId);
                                      }}
                                      className="opacity-0 group-hover/tag:opacity-100 ml-0.5 hover:bg-white/20 rounded-full p-0.5 transition-all"
                                      title="Címke eltávolítása"
                                    >
                                      <XMarkIcon className="w-2 h-2" />
                                    </button>
                                  )}
                                </span>
                              );
                            })}
                            {document.tags.length > 4 && (
                              <span 
                                className="inline-flex items-center px-1 py-0.5 text-[10px] font-medium bg-gray-400 text-white rounded"
                                title={`További ${document.tags.length - 4} címke`}
                              >
                                +{document.tags.length - 4}
                              </span>
                            )}
                            {onAddTag && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowTagInput(document.id);
                                }}
                                className="inline-flex items-center px-1 py-0.5 text-[10px] font-medium text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors"
                                title="Címke hozzáadása"
                              >
                                <TagIcon className="w-2.5 h-2.5 mr-0.5" />+
                              </button>
                            )}
                          </>
                        ) : (
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-gray-400">-</span>
                            {onAddTag && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowTagInput(document.id);
                                }}
                                className="inline-flex items-center px-1 py-0.5 text-[10px] font-medium text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors"
                                title="Címke hozzáadása"
                              >
                                <TagIcon className="w-2.5 h-2.5 mr-0.5" />+
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Size - Kompakt */}
                    <div className="col-span-1">
                      <span className="text-[11px] text-gray-600 dark:text-gray-400">
                        {formatFileSize(document.fileSize)}
                      </span>
                    </div>
                    
                    {/* Type - Kompakt */}
                    <div className="col-span-1">
                      <span 
                        className="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded"
                        style={{ 
                          backgroundColor: fileColor + '20',
                          color: fileColor 
                        }}
                      >
                        {document.fileName?.split('.').pop()?.toUpperCase() || 'FILE'}
                      </span>
                    </div>
                    
                    {/* Modified - Kompakt */}
                    <div className="col-span-2">
                      <span className="text-[11px] text-gray-500 dark:text-gray-400">
                        {formatDate(document.updatedAt)}
                      </span>
                    </div>
                    
                    {/* Actions - Kompakt */}
                    <div className="col-span-1">
                      {/* Jelzés hogy vannak műveletek */}
                      <div className="group-hover:hidden flex items-center justify-center">
                        <div className="flex items-center gap-0.5 text-gray-400 dark:text-gray-500">
                          <div className="w-1 h-1 rounded-full bg-current"></div>
                          <div className="w-1 h-1 rounded-full bg-current"></div>
                          <div className="w-1 h-1 rounded-full bg-current"></div>
                        </div>
                      </div>
                      {/* Műveletek hover-re */}
                      <div className="hidden group-hover:block">
                        <div className="flex items-center gap-0.5 justify-end">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onView(document);
                            }}
                            className="p-1 text-indigo-600 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 rounded transition-colors"
                            title="Részletek"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                          {document.downloadUrl && (document.fileType === 'application/pdf' || document.fileType?.startsWith('image/')) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(document.downloadUrl, '_blank');
                              }}
                              className="p-1 text-purple-600 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded transition-colors"
                              title="Preview (új ablak)"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDownload(document);
                            }}
                            className="p-1 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded transition-colors"
                            title="Letöltés"
                          >
                            <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(document);
                            }}
                            className="p-1 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded transition-colors"
                            title="Szerkesztés"
                          >
                            <PencilSquareIcon className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(document);
                            }}
                            className="p-1 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                            title="Törlés"
                          >
                            <TrashIcon className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Quick Tag Input */}
                {showTagInput === document.id && onAddTag && (
                  <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-purple-50 dark:bg-purple-900/10">
                    <div className="space-y-3">
                      {/* Available Tags */}
                      {documentTags.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Elérhető címkék:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {documentTags.slice(0, 8).map((tag) => (
                              <button
                                key={tag.id}
                                onClick={() => handleAddTag(document, tag.id)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-white rounded-full hover:scale-105 transition-transform duration-200"
                                style={{ 
                                  backgroundColor: tag.color,
                                  color: 'white'
                                }}
                                title={`"${tag.title || tag.name}" hozzáadása (${tag.count} dokumentumban használva)`}
                              >
                                <span className="w-1 h-1 bg-white/30 rounded-full mr-1"></span>
                                {tag.title || tag.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* New Tag Input */}
                      <div>
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Új címke létrehozása:
                        </p>
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && newTag.trim()) {
                                handleAddTag(document, newTag.trim()); // This will be a new tag name
                              } else if (e.key === 'Escape') {
                                setShowTagInput(null);
                                setNewTag('');
                              }
                            }}
                            placeholder="Új címke neve..."
                            className="flex-1 px-3 py-1.5 text-sm border border-purple-300 dark:border-purple-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-purple-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                            autoFocus
                          />
                          <button
                            onClick={() => {
                              if (newTag.trim()) {
                                handleAddTag(document, newTag.trim());
                              }
                            }}
                            disabled={!newTag.trim()}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-md transition-colors"
                            title="Címke hozzáadása"
                          >
                            <PlusIcon className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => {
                              setShowTagInput(null);
                              setNewTag('');
                            }}
                            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md transition-colors"
                            title="Bezárás"
                          >
                            <XMarkIcon className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bulk Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showBulkDeleteModal}
        onClose={() => setShowBulkDeleteModal(false)}
        onConfirm={confirmBulkDelete}
        title="Dokumentumok törlése"
        deleteMessage={`Biztosan törölni szeretné a kiválasztott ${selectedDocuments.length} dokumentumot?`}
        deleteWarning="Ez a művelet nem vonható vissza. A dokumentumok véglegesen törlődnek."
      />
    </div>
  );
}
